import React, { useEffect, useState } from "react";
import { Input } from "antd";
import useCoreStore from "views/hooks/useCoreStore";

interface CounterpartProps {
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
  value?: string;
  limit?: number;
  suffix?: React.ReactNode;
  tabIndex?: number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export default function Counterpart(props: CounterpartProps): React.ReactElement {
  const { onChange, onBlur, value, suffix, ...rest } = props;
  const regexCheckAlphanumeric = /^[a-zA-Z0-9\s+*/()._-]*$/;
  const companyAccountingSettings = useCoreStore(state => state.companyAccountingSettings);

  const prefixClientAccount =
    companyAccountingSettings?.prefixClientAccount ||
    companyAccountingSettings?.software?.defaultPrefixClientAccount ||
    "";

  const maxLength = companyAccountingSettings?.generalAccountsLength || 8;

  const [displayValue, setDisplayValue] = useState("");

  // Calculate the full account value (prefix + code)
  const getFullAccountValue = (inputCode = "") => {
    if (inputCode === null) return prefixClientAccount;
    const fullValue = prefixClientAccount + inputCode;
    if (maxLength && fullValue?.length > maxLength) {
      return fullValue?.slice(0, maxLength);
    }
    return fullValue;
  };

  // Get only the code part (without prefix)
  const getCodeFromValue = (fullValue: string) => {
    if (!fullValue || fullValue === null) return "";
    if (!prefixClientAccount) return fullValue;
    return fullValue?.startsWith(prefixClientAccount)
      ? fullValue?.slice(prefixClientAccount?.length)
      : fullValue;
  };

  useEffect(() => {
    if (value === null || value === undefined) {
      setDisplayValue(prefixClientAccount);
    } else {
      const codeFromValue = getCodeFromValue(value);
      const fullValue = getFullAccountValue(codeFromValue);
      setDisplayValue(fullValue);
    }
  }, [value, prefixClientAccount]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Prevent deletion of prefix
    if (!inputValue || !inputValue?.startsWith(prefixClientAccount)) {
      return;
    }

    // Extract only the code part after the prefix
    const codeOnly = inputValue?.slice(prefixClientAccount?.length);

    // Check if the code part is valid alphanumeric
    if (regexCheckAlphanumeric.test(codeOnly)) {
      const fullValue = getFullAccountValue(codeOnly);

      // Only allow changes if we haven't exceeded max length
      // The combined prefix + code should not exceed maxLength
      if (!maxLength || fullValue?.length <= maxLength) {
        setDisplayValue(fullValue);
        onChange?.(fullValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const cursorPosition = target?.selectionStart || 0;

    // Prevent deletion of prefix characters
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      cursorPosition <= prefixClientAccount?.length
    ) {
      e.preventDefault();
    }

    props.onKeyDown?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const target = e?.target;
    // Set cursor position after the prefix
    setTimeout(() => {
      target.setSelectionRange(prefixClientAccount?.length, target?.value?.length);
    }, 0);
    props.onFocus?.(e);
  };

  return (
    <Input
      data-hj-allow
      style={{ textAlign: "left" }}
      maxLength={maxLength || undefined}
      suffix={suffix}
      onFocus={handleFocus}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      value={displayValue}
      placeholder={prefixClientAccount}
      {...rest}
    />
  );
}
